import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BarChart3, Users, BookOpen, HeartHandshake, Rocket } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Container, Reveal } from "./Motion";
import { FAQ } from "../../data/faq";

export const PILLARS = [
  { icon: ShieldCheck, t: "Protégez vos revenus" },
  { icon: BarChart3, t: "Optimisez votre fiscalité" },
  { icon: Users, t: "Faites croître votre entreprise" },
];

export const Pillars = () => (
  <Container className="py-16">
    <div className="grid gap-10 sm:grid-cols-3">
      {PILLARS.map(({ icon: I, t }, i) => (
        <Reveal key={t} delay={i * 0.1} className="flex flex-col items-center text-center" >
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#650D23]/25 text-[#650D23]">
            <I className="h-7 w-7" strokeWidth={1.4} />
          </span>
          <p className="mt-4 font-display text-xl" data-testid={`pillar-${i}`}>{t}</p>
        </Reveal>
      ))}
    </div>
  </Container>
);

export const WhyStruktiv = () => (
  <section className="bg-[#FAF6F1]" data-testid="why-section">
    <Container className="grid gap-12 py-20 lg:grid-cols-[1fr_1.2fr] lg:py-28">
      <Reveal>
        <p className="eyebrow">Structurer • Protéger • Propulser</p>
        <h2 className="mt-5 text-4xl sm:text-5xl">Pourquoi STRUKTIV?</h2>
      </Reveal>
      <Reveal delay={0.1} className="space-y-5 text-lg leading-relaxed text-[#242020]/85">
        <p>STRUKTIV est un collectif hybride qui combine l'éducation financière, l'accompagnement stratégique et des solutions adaptées aux entrepreneurs.</p>
        <p>Nous accompagnons principalement les professionnels de l'image, de la beauté et du bien-être afin de les aider à mieux comprendre leurs finances, protéger ce qu'ils bâtissent et prendre des décisions éclairées pour leur avenir.</p>
        <p className="font-display text-2xl text-[#650D23]">Notre mission est de rendre la finance plus simple, accessible et humaine.</p>
      </Reveal>
    </Container>
  </section>
);

const PARCOURS = [
  { icon: BookOpen, t: "Apprendre", d: "Des guides, des vidéos et des ressources éducatives pour mieux comprendre ses finances.", to: "/plans-educatifs", id: "apprendre" },
  { icon: HeartHandshake, t: "Être accompagné", d: "Un accompagnement personnalisé pour structurer ses finances et bâtir une stratégie adaptée.", to: "/consultation", id: "accompagne" },
  { icon: Rocket, t: "Passer à l'action", d: "Des solutions concrètes pour protéger ses revenus, optimiser sa fiscalité et soutenir sa croissance.", to: "/secteurs", id: "action" },
];

export const Parcours = () => (
  <Container className="py-20 lg:py-28">
    <Reveal><p className="eyebrow">Trois parcours</p><h2 className="mt-4 max-w-xl text-3xl sm:text-4xl">Avancez à votre rythme, selon vos besoins.</h2></Reveal>
    <div className="mt-12 grid gap-6 md:grid-cols-3">
      {PARCOURS.map(({ icon: I, t, d, to, id }, i) => (
        <Reveal key={id} delay={i * 0.1}>
          <Link to={to} data-testid={`parcours-card-${id}`} className="card-soft group flex h-full flex-col p-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#650D23] text-[#FFFDFB]"><I className="h-5 w-5" /></span>
            <h3 className="mt-6 text-2xl uppercase tracking-wide">{t}</h3>
            <p className="mt-3 flex-1 text-[#242020]/75">{d}</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#650D23]">Découvrir <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
          </Link>
        </Reveal>
      ))}
    </div>
  </Container>
);

export const FaqAccordion = () => (
  <Accordion type="single" collapsible className="divide-y divide-[#EDE3D9] rounded-2xl border border-[#EDE3D9] bg-[#FFFDFB] px-6" data-testid="faq-accordion">
    {FAQ.map((f, i) => (
      <AccordionItem key={i} value={`q${i}`} className="border-0">
        <AccordionTrigger className="py-5 text-left font-display text-lg hover:no-underline" data-testid={`faq-question-${i}`}>{i + 1}. {f.q}</AccordionTrigger>
        <AccordionContent className="pb-5 text-[.98rem] leading-relaxed text-[#242020]/80" data-testid={`faq-answer-${i}`}>{f.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const PageHeader = ({ eyebrow, title, accent, children, image }) => (
  <section className="wave-bg relative overflow-hidden">
    <Container className={`grid items-center gap-10 py-16 lg:py-24 ${image ? "lg:grid-cols-2" : ""}`}>
      <Reveal className="space-y-6">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1 className="text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">{title} {accent && <span className="text-[#650D23]">{accent}</span>}</h1>
        {children}
      </Reveal>
      {image && (
        <Reveal delay={0.1}><img src={image} alt="" className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-[0_30px_60px_-35px_rgba(74,12,27,.5)]" /></Reveal>
      )}
    </Container>
  </section>
);

export const TopicGrid = ({ items, testid }) => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid={testid}>
    {items.map(({ icon: I, t, d }, i) => (
      <Reveal key={t} delay={(i % 4) * 0.07}>
        <div className="card-soft h-full p-6">
          <I className="h-6 w-6 text-[#650D23]" strokeWidth={1.5} />
          <h3 className="mt-4 text-lg">{t}</h3>
          {d && <p className="mt-2 text-sm text-[#242020]/70">{d}</p>}
        </div>
      </Reveal>
    ))}
  </div>
);
